import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  getActiveSession,
  startSession,
  saveProgress,
  completeSession,
  getSessionHistory,
} from "@/lib/exercise.functions";
import {
  Wind,
  Heart,
  Eye,
  Pencil,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Check,
  RotateCcw,
} from "lucide-react";

const SLUG = "nervous-system-reset";

export const Route = createFileRoute("/reset")({
  head: () => ({
    meta: [
      { title: "Nervous System Reset — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A 5-step gentle reset for your nervous system. Save and resume anytime.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ResetPage,
});

type Step = {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  body: React.ReactNode;
};

function ResetPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) navigate({ to: "/login", search: { redirect: "/reset" } });
      else setAuthed(true);
    });
  }, [navigate]);

  if (!authed) return <div className="min-h-[60vh]" />;
  return <ResetExercise />;
}

function ResetExercise() {
  const qc = useQueryClient();
  const fetchActive = useServerFn(getActiveSession);
  const fetchHistory = useServerFn(getSessionHistory);
  const startFn = useServerFn(startSession);
  const saveFn = useServerFn(saveProgress);
  const completeFn = useServerFn(completeSession);

  const { data: activeData, isLoading } = useQuery({
    queryKey: ["exercise-active", SLUG],
    queryFn: () => fetchActive({ data: { slug: SLUG } }),
  });
  const { data: historyData } = useQuery({
    queryKey: ["exercise-history", SLUG],
    queryFn: () => fetchHistory({ data: { slug: SLUG } }),
  });

  const session = activeData?.session;
  const [step, setStep] = useState(0);
  const [notes, setNotes] = useState("");
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const hydrated = useRef(false);

  useEffect(() => {
    if (session && !hydrated.current) {
      setStep(session.step_index);
      setNotes(session.notes || "");
      hydrated.current = true;
    }
  }, [session]);

  const startMutation = useMutation({
    mutationFn: () => startFn({ data: { slug: SLUG } }),
    onSuccess: () => {
      hydrated.current = false;
      setStep(0);
      setNotes("");
      qc.invalidateQueries({ queryKey: ["exercise-active", SLUG] });
    },
  });

  const saveMutation = useMutation({
    mutationFn: (vars: { step_index: number; notes?: string }) =>
      saveFn({ data: { id: session!.id, ...vars } }),
    onSuccess: () => setSavedAt(new Date()),
  });

  const completeMutation = useMutation({
    mutationFn: () => completeFn({ data: { id: session!.id, notes } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exercise-active", SLUG] });
      qc.invalidateQueries({ queryKey: ["exercise-history", SLUG] });
    },
  });

  // Autosave notes (debounced)
  useEffect(() => {
    if (!session) return;
    const t = setTimeout(() => {
      saveMutation.mutate({ step_index: step, notes });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  if (isLoading) return <div className="min-h-[60vh]" />;

  // No active session → start screen
  if (!session) {
    return (
      <Shell>
        <StartScreen
          onStart={() => startMutation.mutate()}
          loading={startMutation.isPending}
          history={historyData?.sessions ?? []}
        />
      </Shell>
    );
  }

  const steps = buildSteps(notes, setNotes);
  const current = steps[step];
  const isLast = step === steps.length - 1;

  const goNext = () => {
    const next = Math.min(step + 1, steps.length - 1);
    setStep(next);
    saveMutation.mutate({ step_index: next, notes });
  };
  const goPrev = () => {
    const prev = Math.max(step - 1, 0);
    setStep(prev);
    saveMutation.mutate({ step_index: prev, notes });
  };
  const finish = () => completeMutation.mutate();

  return (
    <Shell>
      {/* Progress */}
      <div className="mb-8 flex items-center gap-2">
        {steps.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all ${
              i <= step ? "bg-[var(--gradient-gold)]" : "bg-border"
            }`}
            style={i <= step ? { background: "var(--gradient-gold)" } : {}}
          />
        ))}
      </div>

      <div className="rounded-3xl border border-border/50 bg-card p-7 md:p-10">
        <div className="flex items-center gap-3 text-[var(--gold-deep)]">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cream/70">
            <current.icon className="h-5 w-5" />
          </div>
          <span className="text-xs uppercase tracking-widest text-muted-foreground">
            Step {step + 1} of {steps.length}
          </span>
        </div>
        <h2 className="mt-4 font-serif text-3xl text-foreground">{current.title}</h2>
        <p className="mt-2 text-muted-foreground">{current.subtitle}</p>
        <div className="mt-6">{current.body}</div>

        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={goPrev}
            disabled={step === 0}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          <p className="text-xs text-muted-foreground">
            {savedAt
              ? `Saved · ${savedAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`
              : "Progress saves automatically"}
          </p>

          {isLast ? (
            <button
              onClick={finish}
              disabled={completeMutation.isPending}
              className="btn-gold"
            >
              <Check className="h-4 w-4" /> Complete
            </button>
          ) : (
            <button onClick={goNext} className="btn-gold">
              Next <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {completeMutation.isSuccess && (
        <div className="mt-6 rounded-2xl border border-gold/40 bg-cream/60 p-6 text-center">
          <Sparkles className="mx-auto mb-2 h-5 w-5 text-[var(--gold-deep)]" />
          <p className="font-serif text-xl text-foreground">Well done.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            You showed up for yourself today.
          </p>
          <div className="mt-4 flex justify-center gap-3">
            <button
              onClick={() => {
                completeMutation.reset();
                startMutation.mutate();
              }}
              className="btn-gold-outline"
            >
              <RotateCcw className="h-4 w-4" /> Begin again
            </button>
            <Link to="/" className="btn-gold">
              Back home
            </Link>
          </div>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Home
        </Link>
      </div>
      <h1 className="font-serif text-4xl text-foreground">Nervous System Reset</h1>
      <p className="mt-2 text-muted-foreground">
        A gentle 5-step practice to help your body feel safer. Save and resume
        anytime.
      </p>
      <div className="mt-8">{children}</div>
    </div>
  );
}

function StartScreen({
  onStart,
  loading,
  history,
}: {
  onStart: () => void;
  loading: boolean;
  history: { id: string; completed_at: string | null }[];
}) {
  return (
    <div className="rounded-3xl border border-border/50 bg-card p-8 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
        <Wind className="h-7 w-7 text-foreground" />
      </div>
      <h2 className="font-serif text-2xl text-foreground">Ready to begin?</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Find a quiet place. This takes about 5–8 minutes. You can pause and come
        back to where you left off anytime.
      </p>
      <button onClick={onStart} disabled={loading} className="btn-gold mt-6">
        Begin practice <ArrowRight className="h-4 w-4" />
      </button>

      {history.length > 0 && (
        <div className="mt-8 border-t border-border/40 pt-6 text-start">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Past sessions
          </p>
          <ul className="mt-3 space-y-2">
            {history.slice(0, 5).map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between rounded-lg bg-cream/50 px-3 py-2 text-sm"
              >
                <span className="text-foreground">
                  {s.completed_at
                    ? new Date(s.completed_at).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })
                    : "—"}
                </span>
                <Check className="h-4 w-4 text-[var(--gold-deep)]" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ----------------------- Step content ----------------------- */

function buildSteps(notes: string, setNotes: (v: string) => void): Step[] {
  return [
    {
      title: "Arrive",
      subtitle: "Notice where you are. You don't have to change anything.",
      icon: Heart,
      body: (
        <div className="rounded-2xl bg-cream/60 p-6 text-sm leading-relaxed text-foreground">
          <p>
            Place both feet on the floor. Let your shoulders drop. Take one
            slow breath in through your nose, and a longer breath out through
            your mouth.
          </p>
          <p className="mt-3 text-muted-foreground">
            Nothing to fix here — just arriving.
          </p>
        </div>
      ),
    },
    {
      title: "Box breathing",
      subtitle: "Inhale 4 · hold 4 · exhale 4 · hold 4. Repeat four times.",
      icon: Wind,
      body: <BoxBreathing />,
    },
    {
      title: "5-4-3-2-1 grounding",
      subtitle: "Use your senses to come back to the present.",
      icon: Eye,
      body: (
        <div className="space-y-3 text-sm">
          {[
            { n: 5, t: "things you can see" },
            { n: 4, t: "things you can touch" },
            { n: 3, t: "things you can hear" },
            { n: 2, t: "things you can smell" },
            { n: 1, t: "thing you can taste" },
          ].map((row) => (
            <div
              key={row.n}
              className="flex items-center gap-3 rounded-xl bg-cream/60 px-4 py-3"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--gradient-gold)] font-serif text-base text-foreground">
                {row.n}
              </span>
              <span className="text-foreground">{row.t}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Gentle reflection",
      subtitle: "What's one word for how your body feels now?",
      icon: Pencil,
      body: (
        <div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            placeholder="Soft, tired, steadier, tender, okay…"
            className="w-full resize-none rounded-2xl border border-border bg-background p-4 text-sm leading-relaxed text-foreground outline-none transition-colors focus:border-gold/60"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Saved privately to your account.
          </p>
        </div>
      ),
    },
    {
      title: "Close with kindness",
      subtitle: "Place a hand on your heart. Acknowledge that you showed up.",
      icon: Sparkles,
      body: (
        <div className="rounded-2xl bg-cream/60 p-6 text-sm leading-relaxed text-foreground">
          <p>
            You took time to care for yourself today. That matters — even on
            days it doesn't feel like enough.
          </p>
          <p className="mt-3 italic text-muted-foreground">
            "Healing happens in moments, not milestones."
          </p>
        </div>
      ),
    },
  ];
}

/* ----------------------- Box breathing animation ----------------------- */

function BoxBreathing() {
  const phases = ["Inhale", "Hold", "Exhale", "Hold"];
  const [phase, setPhase] = useState(0);
  const [count, setCount] = useState(4);

  useEffect(() => {
    const tick = setInterval(() => {
      setCount((c) => {
        if (c <= 1) {
          setPhase((p) => (p + 1) % 4);
          return 4;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  return (
    <div className="flex flex-col items-center gap-4 py-2">
      <div
        className="relative flex h-44 w-44 items-center justify-center rounded-full transition-transform duration-1000 ease-in-out"
        style={{
          background: "var(--gradient-gold)",
          boxShadow: "var(--shadow-gold)",
          transform:
            phase === 0
              ? "scale(1.08)"
              : phase === 2
              ? "scale(0.85)"
              : "scale(1)",
        }}
      >
        <div className="text-center">
          <p className="font-serif text-2xl text-foreground">{phases[phase]}</p>
          <p className="mt-1 text-3xl font-light text-foreground/80">{count}</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Follow the circle. Soft and slow.
      </p>
    </div>
  );
}
